import { createTheme } from '@mui/material/styles';

export type InputType =
  | 'slider'
  | 'text'
  | 'textarea'
  | 'color'
  | 'number'
  | 'json';

export interface FieldConfig {
  key: string;
  label: string;
  type: InputType;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

export interface SectionConfig {
  id: string;
  title: string;
  fields: FieldConfig[];
  defaultValues: Record<string, any>;
}

const muiDefaultTheme = createTheme();

export const themeMakerConfig: SectionConfig[] = [
  {
    id: 'breakpoints',
    title: 'Customize Breakpoints',
    fields: Object.keys(muiDefaultTheme.breakpoints.values).map((bp) => ({
      key: bp,
      label: bp.toUpperCase(),
      type: 'slider',
      min: 0,
      max: 3000,
      step: 10,
    })),
    defaultValues: muiDefaultTheme.breakpoints.values,
  },
  {
    id: 'registration',
    title: 'Registration',
    fields: [
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'creator', label: 'Creator', type: 'text', disabled: true },
      { key: 'type', label: 'Type', type: 'text' },
    ],
    defaultValues: {
      name: 'Custom Theme',
      description: 'Fully customized MUI theme',
      creator: '',
      type: 'theme',
    },
  },
  {
    id: 'palette',
    title: 'Palette',
    fields: [
      { key: 'primary.main', label: 'Primary', type: 'color' },
      { key: 'secondary.main', label: 'Secondary', type: 'color' },
      { key: 'error.main', label: 'Error', type: 'color' },
      { key: 'warning.main', label: 'Warning', type: 'color' },
      { key: 'info.main', label: 'Info', type: 'color' },
      { key: 'success.main', label: 'Success', type: 'color' },
      { key: 'background.default', label: 'Background', type: 'color' },
      { key: 'background.paper', label: 'Paper', type: 'color' },
      { key: 'text.primary', label: 'Text Primary', type: 'color' },
      { key: 'text.secondary', label: 'Text Secondary', type: 'color' },
    ],
    defaultValues: {
      primary: { main: muiDefaultTheme.palette.primary.main },
      secondary: { main: muiDefaultTheme.palette.secondary.main },
      error: { main: muiDefaultTheme.palette.error.main },
      warning: { main: muiDefaultTheme.palette.warning.main },
      info: { main: muiDefaultTheme.palette.info.main },
      success: { main: muiDefaultTheme.palette.success.main },
      background: {
        default: muiDefaultTheme.palette.background.default,
        paper: muiDefaultTheme.palette.background.paper,
      },
      text: {
        primary: muiDefaultTheme.palette.text.primary,
        secondary: muiDefaultTheme.palette.text.secondary,
      },
    },
  },
  {
    id: 'typography',
    title: 'Typography',
    fields: [
      { key: 'fontSize', label: 'Base Font Size', type: 'number' },
      { key: 'fontFamily', label: 'Font Family', type: 'text' },
      { key: 'h1.fontSize', label: 'H1 Size', type: 'number' },
      { key: 'h2.fontSize', label: 'H2 Size', type: 'number' },
      { key: 'button.textTransform', label: 'Button Transform', type: 'text' },
    ],
    defaultValues: {
      fontSize: muiDefaultTheme.typography.fontSize,
      fontFamily: muiDefaultTheme.typography.fontFamily,
      h1: { fontSize: muiDefaultTheme.typography.h1.fontSize },
      h2: { fontSize: muiDefaultTheme.typography.h2.fontSize },
      button: {
        textTransform: muiDefaultTheme.typography.button.textTransform,
      },
    },
  },
  {
    id: 'shape',
    title: 'Shape',
    fields: [{ key: 'borderRadius', label: 'Border Radius', type: 'number' }],
    defaultValues: {
      borderRadius: muiDefaultTheme.shape.borderRadius,
    },
  },
  {
    id: 'spacing',
    title: 'Spacing',
    fields: [{ key: 'unit', label: 'Base Spacing Unit', type: 'number' }],
    defaultValues: {
      unit: parseInt(muiDefaultTheme.spacing(1).replace('px', ''), 10),
    },
  },
  {
    id: 'zIndex',
    title: 'Z-Index Levels',
    fields: Object.keys(muiDefaultTheme.zIndex).map((z) => ({
      key: z,
      label: z,
      type: 'number',
    })),
    defaultValues: muiDefaultTheme.zIndex,
  },
  {
    id: 'transitions',
    title: 'Transitions',
    fields: [
      { key: 'duration.shortest', label: 'Duration Shortest', type: 'number' },
      { key: 'duration.shorter', label: 'Duration Shorter', type: 'number' },
      { key: 'duration.short', label: 'Duration Short', type: 'number' },
      { key: 'duration.standard', label: 'Duration Standard', type: 'number' },
      { key: 'easing.easeInOut', label: 'Ease In Out', type: 'text' },
      { key: 'easing.easeOut', label: 'Ease Out', type: 'text' },
      { key: 'easing.easeIn', label: 'Ease In', type: 'text' },
      { key: 'easing.sharp', label: 'Sharp', type: 'text' },
    ],
    defaultValues: muiDefaultTheme.transitions,
  },
  {
    id: 'mixins',
    title: 'Mixins',
    fields: [
      { key: 'toolbar.minHeight', label: 'Toolbar Min Height', type: 'number' },
    ],
    defaultValues: muiDefaultTheme.mixins,
  },
  {
    id: 'components',
    title: 'Component Overrides',
    fields: [
      {
        key: 'root',
        label: 'MUI Component Overrides (JSON)',
        type: 'json',
      },
    ],
    defaultValues: muiDefaultTheme.components || {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            textTransform: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: muiDefaultTheme.palette.primary.main,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 4,
            fontSize: '0.75rem',
          },
        },
      },
    },
  },
];
